import React, { useMemo } from 'react';
import { DownloadIcon, MailIcon } from 'lucide-react';
import { policyMeta, policySections } from '../data/privacyPolicy';
import { PolicyContents } from '../components/legal/PolicyContents';
import { PolicySectionBody } from '../components/legal/PolicySectionBody';
import { BackToTop } from '../components/legal/BackToTop';
import { SiteFooter } from '../components/ui/SiteFooter';
import { useActiveSection } from '../hooks/useActiveSection';
const metaLinkStyles = 'inline-flex items-center gap-2 text-sm font-medium text-purple-700 underline underline-offset-4 decoration-purple-300 hover:text-purple-900 hover:decoration-purple-700 transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 focus-visible:ring-offset-ivory rounded';
export function PrivacyPolicyPage() {
  const sectionIds = useMemo(() => policySections.map(section => section.id), []);
  const activeId = useActiveSection(sectionIds);
  return <div className="min-h-screen bg-ivory font-sans text-charcoal selection:bg-purple-200 selection:text-charcoal" data-id="element-755" data-mp="29q1xxoxg7z49">
      {/* Navigation */}
      <nav className="sticky top-0 left-0 right-0 z-50 px-6 py-6 flex justify-between items-center max-w-7xl mx-auto bg-ivory/95 backdrop-blur-sm border-b border-charcoal/5" data-id="element-756" data-mp="2jpssfqm0ee4c">
        <a href="/" aria-label="Fem Entity home" data-id="element-757" data-mp="2eomzbrofsoua">
          <img src="https://cdn.magicpatterns.com/uploads/xpYure8muF9bisffxXgVg2/fementity.png" alt="Fem Entity" className="h-8 md:h-10" data-id="element-758" data-mp="1r0c5h8h5rpfi" />
        </a>
        <div className="hidden md:flex space-x-8 text-sm font-medium text-charcoal/80" data-id="element-759" data-mp="20rgfruvw2m40">
          <a href="/" className="hover:text-purple-700 transition-colors" data-id="element-760" data-mp="1w0hxiprcbdy4">
            Home
          </a>
          <a href="/get-the-app" className="hover:text-purple-700 transition-colors" data-id="element-761" data-mp="18a43wut3x71f">
            Download the App
          </a>
          <a href="/practitioners" className="hover:text-purple-700 transition-colors" data-id="element-762" data-mp="139q8cc8ozpka">
            Practitioners
          </a>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-763" data-mp="1wz74wqvn2iun">
        {/* Title area */}
        <header className="pt-16 md:pt-24 pb-10 md:pb-14 border-b border-charcoal/10" data-id="element-764" data-mp="21sjrywko6hla">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-700 mb-5" data-id="element-765" data-mp="26ps2cazkthbz">
            {policyMeta.company}
          </p>
          <h1 className="font-serif text-4xl md:text-6xl text-charcoal leading-[1.05] tracking-tight" data-id="element-766" data-mp="1e2fr2wyv8vxz">
            {policyMeta.title}
          </h1>
          <p className="mt-6 max-w-[62ch] text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-767" data-mp="1j0r0x7p5v6g7">
            {policyMeta.summary}
          </p>

          <dl className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm" data-id="element-768" data-mp="1ny922bwxshdn">
            <div className="flex items-baseline gap-2" data-id="element-769" data-mp="2uis0rxtbfpn7">
              <dt className="text-charcoal/50" data-id="element-770" data-mp="2zepj29o5362m">Version</dt>
              <dd className="font-medium text-charcoal" data-id="element-771" data-mp="nsf9i5i3zcw4">
                {policyMeta.version.replace('Version ', '')}
              </dd>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-772" data-mp="iunakrj1qqif">
              <dt className="text-charcoal/50" data-id="element-773" data-mp="e2c8pj4qwun8">Effective</dt>
              <dd className="font-medium text-charcoal" data-id="element-774" data-mp="e0snrf859vf4">September 2026</dd>
            </div>
          </dl>

          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4" data-id="element-775" data-mp="91g4f4ghf6ha">
            <a href={policyMeta.pdfUrl} target="_blank" rel="noopener noreferrer" className={metaLinkStyles} data-id="element-776" data-mp="53idnx72ccbs">
              <DownloadIcon aria-hidden="true" className="w-4 h-4" data-id="element-777" data-mp="3ra6w14dh74mx" />
              Download the PDF
            </a>
            <a href={`mailto:${policyMeta.email}`} className={metaLinkStyles} data-id="element-778" data-mp="3mblun1mhmeww">
              <MailIcon aria-hidden="true" className="w-4 h-4" data-id="element-779" data-mp="1pgpqq7ctifvc" />
              {policyMeta.email}
            </a>
          </div>
        </header>

        {/* Index + body */}
        <div className="lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-14 xl:gap-20 pb-24 md:pb-32" data-id="element-780" data-mp="1sjnaeyifedbf">
          <PolicyContents sections={policySections} activeId={activeId} data-id="element-781" data-mp="1xj03w23syimz" />

          <article className="max-w-[68ch] pt-10 lg:pt-14 divide-y divide-charcoal/10" data-id="element-782" data-mp="1il7rhitjyad0">
            {policySections.map(section => <PolicySectionBody key={section.id} section={section} data-id="element-783" data-mp="1njhkpnvpv0vz" />)}

            <p className="pt-12 text-sm text-charcoal/50" data-id="element-784" data-mp="19schycx1xshf">
              © {new Date().getFullYear()} Fem Entity Inc. All rights reserved.{' '}
              {policyMeta.version} — {policyMeta.effectiveDate}.
            </p>
          </article>
        </div>
      </main>

      <BackToTop data-id="element-785" data-mp="zsvfs5h0dwq7" />

      <SiteFooter currentPath="/privacy-policy" data-id="element-786" data-mp="14tjeldbhbhuz" />
    </div>;
}